/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Form, Input, Upload, message, Row, Col, Button as AntButton, Divider } from 'antd';
import { UploadOutlined, DeleteOutlined } from '@ant-design/icons';
import { Button } from '../../design-system';
import { BUTTON_PRESETS } from '../../design-system/constants';
import ApiServiceNode from '../../service/ApiServiceNode';
import { getApiErrorMessage } from '../../utils/errorUtils';
import logger from '../../utils/logger';



const { TextArea } = Input;

const CompanySettings = () => {
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [company, setCompany] = useState(null);
    const [logoFile, setLogoFile] = useState(null);
    const [pdfFile, setPdfFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);

    useEffect(() => {
        fetchCompanyData();
    }, []);

    const fetchCompanyData = async () => {
        try {
            setLoading(true);
            const response = await ApiServiceNode.get('/settings/company');
            const companyData = response.data.data?.company || response.data.company;
            setCompany(companyData);

            // Set logo preview if exists
            if (companyData.logo) {
                setLogoPreview(companyData.logo);
            }

            // Populate form
            form.setFieldsValue({
                full_name: companyData.full_name || '',
                introduction: companyData.introduction || '',
                facebook: companyData.social_media?.facebook || '',
                instagram: companyData.social_media?.instagram || '',
                linkedin: companyData.social_media?.linkedin || '',
                tiktok: companyData.social_media?.tiktok || '',
                twitter: companyData.social_media?.twitter || '',
                wechat: companyData.social_media?.wechat || '',
                whatsapp: companyData.social_media?.whatsapp || ''
            });
        } catch (error) {
            logger.error('Error fetching company data:', error);
            message.error('Failed to load company data');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            // Update company basic info + social media
            await ApiServiceNode.put(`/settings/company/${company.id}`, {
                full_name: values.full_name,
                introduction: values.introduction,
                social_media: {
                    facebook: values.facebook || '',
                    instagram: values.instagram || '',
                    linkedin: values.linkedin || '',
                    tiktok: values.tiktok || '',
                    twitter: values.twitter || '',
                    wechat: values.wechat || '',
                    whatsapp: values.whatsapp || ''
                }
            });

            // Upload logo if changed
            if (logoFile) {
                const formData = new FormData();
                formData.append('logo', logoFile);
                formData.append('company_id', company.id);
                await ApiServiceNode.post('/settings/company/upload-logo', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                });
            }

            // Upload PDF if changed
            if (pdfFile) {
                const formData = new FormData();
                formData.append('starter_guide', pdfFile);
                formData.append('company_id', company.id);
                await ApiServiceNode.post('/settings/company/upload-starter-guide', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                });
            }

            message.success('Company settings updated successfully');

            // Reset file states and refresh data
            setLogoFile(null);
            setPdfFile(null);
            await fetchCompanyData();
        } catch (error) {
            logger.error('Error updating company settings:', error);
            message.error(getApiErrorMessage(error, 'Failed to update company settings'));
        } finally {
            setLoading(false);
        }
    };

    const handleLogoChange = (info) => {
        const file = info.file.originFileObj || info.file;

        // Validate file type
        const isImage = file.type.startsWith('image/');
        if (!isImage) {
            message.error('You can only upload image files!');
            return;
        }

        // Validate file size (5MB)
        const isLt5M = file.size / 1024 / 1024 < 5;
        if (!isLt5M) {
            message.error('Image must be smaller than 5MB!');
            return;
        }

        setLogoFile(file);

        // Create preview
        const reader = new FileReader();
        reader.onload = (e) => {
            setLogoPreview(e.target.result);
        };
        reader.readAsDataURL(file);
    };

    const handlePdfChange = (info) => {
        const file = info.file.originFileObj || info.file;

        // Validate file type
        const isPdf = file.type === 'application/pdf';
        if (!isPdf) {
            message.error('You can only upload PDF files!');
            return;
        }

        // Validate file size (20MB)
        const isLt20M = file.size / 1024 / 1024 < 20;
        if (!isLt20M) {
            message.error('PDF must be smaller than 20MB!');
            return;
        }

        setPdfFile(file);
        message.info(`Selected: ${file.name}`);
    };

    const handleDeleteStarterGuide = async () => {
        try {
            await ApiServiceNode.delete(`/settings/company/starter-guide/${company.id}`);
            message.success('Starter guide deleted successfully');
            await fetchCompanyData();
        } catch (error) {
            logger.error('Error deleting starter guide:', error);
            message.error('Failed to delete starter guide');
        }
    };

    if (!company && loading) {
        return (
            <div className="wrapper-grey finance-container">
                <Card loading={true}>Loading company settings...</Card>
            </div>
        );
    }

    return (
        <div className="wrapper-grey finance-container">
            <Card>
                <div className="section-header">
                    <div className="header-left">
                        <h4>EDIT COMPANY</h4>
                    </div>
                </div>

                <Form form={form} layout="vertical">
                    <Row gutter={24}>
                        {/* Left Column - Basic Information & Social Media */}
                        <Col xs={24} lg={14}>
                            <Card title="Company Information" style={{ marginBottom: 24 }}>
                                <Form.Item
                                    label="Company Name"
                                    name="full_name"
                                    rules={[{ required: true, message: 'Please enter company name' }]}
                                >
                                    <Input placeholder="Enter company name" maxLength={64} />
                                </Form.Item>

                                <Form.Item label="Company Introduction" name="introduction">
                                    <TextArea rows={5} placeholder="Enter a brief description about your company..." />
                                </Form.Item>

                                <Divider>Social Media Links</Divider>

                                <Row gutter={[16, 0]}>
                                    <Col xs={24} sm={12}>
                                        <Form.Item label="Facebook" name="facebook">
                                            <Input placeholder="facebook.com/yourcompany" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12}>
                                        <Form.Item label="Instagram" name="instagram">
                                            <Input placeholder="@yourcompany" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12}>
                                        <Form.Item label="LinkedIn" name="linkedin">
                                            <Input placeholder="linkedin.com/company/yourcompany" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12}>
                                        <Form.Item label="Twitter" name="twitter">
                                            <Input placeholder="@yourcompany" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12}>
                                        <Form.Item label="TikTok" name="tiktok">
                                            <Input placeholder="@yourcompany" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12}>
                                        <Form.Item label="WeChat" name="wechat">
                                            <Input placeholder="yourcompany-wechat" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12}>
                                        <Form.Item label="WhatsApp" name="whatsapp">
                                            <Input placeholder="+60123456789" />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Card>
                        </Col>

                        {/* Right Column - Logo & Files */}
                        <Col xs={24} lg={10}>
                            {/* Company Logo Card */}
                            <Card title="Company Logo" style={{ marginBottom: 24 }}>
                                <div style={{ textAlign: 'center' }}>
                                    {logoPreview && (
                                        <div style={{
                                            marginBottom: 16,
                                            padding: 20,
                                            background: '#fafafa',
                                            borderRadius: 8,
                                            border: '2px dashed #d9d9d9'
                                        }}>
                                            <img
                                                src={logoPreview}
                                                alt="Company logo"
                                                style={{
                                                    maxWidth: '100%',
                                                    maxHeight: 120,
                                                    objectFit: 'contain'
                                                }}
                                            />
                                        </div>
                                    )}
                                    <Upload
                                        beforeUpload={() => false}
                                        onChange={handleLogoChange}
                                        showUploadList={false}
                                        accept="image/jpeg,image/png,image/gif,image/webp"
                                    >
                                        <AntButton icon={<UploadOutlined />} style={{ width: '100%' }}>
                                            {logoFile ? `Selected: ${logoFile.name}` : 'Choose Logo...'}
                                        </AntButton>
                                    </Upload>
                                    <p style={{ fontSize: 12, color: '#666', marginTop: 8, marginBottom: 0 }}>
                                        JPG, PNG, GIF, or WebP • Max 5MB
                                    </p>
                                </div>
                            </Card>

                            {/* Starter Guide Card */}
                            <Card title="Starter Guide" style={{ marginBottom: 24 }}>
                                <Form.Item style={{ marginBottom: 0 }}>
                                    {company?.starter_guide ? (
                                        <div style={{
                                            padding: 12,
                                            background: '#f5f5f5',
                                            borderRadius: 4,
                                            marginBottom: 12,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between'
                                        }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                <i className="fa fa-file-pdf-o" style={{ fontSize: 20, color: '#d32f2f' }}></i>
                                                <a
                                                    href={company.starter_guide}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{ color: '#1890ff', textDecoration: 'underline' }}
                                                >
                                                    View Current PDF
                                                </a>
                                            </div>
                                            <AntButton
                                                type="link"
                                                danger
                                                icon={<DeleteOutlined />}
                                                onClick={handleDeleteStarterGuide}
                                                size="small"
                                            >
                                                Remove
                                            </AntButton>
                                        </div>
                                    ) : (
                                        <div style={{
                                            padding: 24,
                                            textAlign: 'center',
                                            background: '#fafafa',
                                            borderRadius: 6,
                                            marginBottom: 16,
                                            border: '2px dashed #d9d9d9'
                                        }}>
                                            <i className="fa fa-file-pdf-o" style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 8 }}></i>
                                            <br />
                                            <span style={{ color: '#666' }}>No starter guide uploaded</span>
                                        </div>
                                    )}
                                    <Upload
                                        beforeUpload={() => false}
                                        onChange={handlePdfChange}
                                        showUploadList={false}
                                        accept="application/pdf"
                                    >
                                        <AntButton icon={<UploadOutlined />} style={{ width: '100%' }}>
                                            {pdfFile ? `Selected: ${pdfFile.name}` : 'Choose PDF...'}
                                        </AntButton>
                                    </Upload>
                                    <p style={{ fontSize: 12, color: '#666', marginTop: 8, marginBottom: 0 }}>
                                        Will be used as the default starter guide when an office has not uploaded one. Max size 20MB. Supported file: .pdf
                                    </p>
                                </Form.Item>
                            </Card>
                        </Col>
                    </Row>

                    <div style={{ marginTop: 24, display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                        <Button {...BUTTON_PRESETS.SAVE} onClick={handleSubmit} loading={loading}>
                            Save Changes
                        </Button>
                        <Button {...BUTTON_PRESETS.CANCEL} onClick={() => navigate('/admin/office')}>
                            Cancel
                        </Button>
                    </div>
                </Form>
            </Card>
        </div>
    );
};

export default CompanySettings;
